<template>
  <div>
    <div class="flex items-center">
      <label
        v-if="field.type !== 'checkbox' && field.type !== 'radio'"
        :for="field.id"
        class="block text-sm font-medium text-gray-700"
      >
        {{ field.label }}<span v-if="field.required">*</span>
      </label>
      <button
        v-if="
          field.extra_information &&
          field.type !== 'checkbox' &&
          field.type !== 'radio'
        "
        type="button"
        @click="toggleExtraInformation"
        class="ml-2"
        :aria-label="
          $t('form.show_extra_information_for', {
            value: field.label,
          })
        "
        :aria-expanded="showExtraInformation ? 'true' : 'false'"
        :aria-controls="`${field.id}-extra-information`"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
    </div>
    <div class="mt-1 sm:mt-0 sm:col-span-3">
      <div v-if="showExtraInformation" :id="`${field.id}-extra-information`">
        <VueMarkdown
          class="mt-1 mb-2 text-sm text-gray-700 prose max-w-none"
          :source="field.extra_information"
        />
      </div>
      <VueMarkdown
        v-if="field.description"
        class="mt-1 mb-2 text-sm text-gray-700 prose max-w-none"
        :source="field.description"
      />
      <!-- eslint-disable vue/no-mutating-props -->
      <ValidationProvider
        v-slot="{ errors, failed, ariaMsg, ariaInput }"
        :vid="field.id"
        :name="field.label"
        v-if="field.type == 'longtext'"
      >
        <textarea
          rows="3"
          class="form-textarea block w-full border-gray-500 focus:border-blue-600 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          :id="field.id"
          :name="field.id"
          :required="field.required ? true : false"
          :aria-describedby="field.helper ? `${field.id}-helper` : null"
          :disabled="disabled"
          :maxlength="field.maxlength > 0 ? field.maxlength : null"
          v-model="response[field.id]"
          v-bind="ariaInput"
          :aria-invalid="failed"
          @change="onChange"
        />
        <span
          v-bind="ariaMsg"
          aria-live="assertive"
          class="text-sm text-red-700"
        >
          {{ errors[0] }}
        </span>
      </ValidationProvider>

      <div v-if="field.maxlength > 0" class="text-sm text-right text-gray-700">
        <span class="sr-only" aria-live="polite">
          {{
            $t('form.text_length_indicator', {
              current: (response[field.id] || '').length,
              max: field.maxlength,
            })
          }}
        </span>
        <span aria-hidden="true">
          {{ (response[field.id] || '').length }}/{{ field.maxlength }}
        </span>
      </div>
      <!-- eslint-disable vue/no-mutating-props -->
      <ValidationProvider
        v-slot="{ errors, failed, ariaMsg, ariaInput }"
        :vid="field.id"
        :name="field.label"
        v-if="field.type == 'dropdown'"
      >
        <select
          class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-600 sm:text-sm sm:leading-5"
          :id="field.id"
          :name="field.id"
          :required="field.required"
          :aria-describedby="field.helper ? `${field.id}-helper` : null"
          :disabled="disabled"
          v-model="response[field.id]"
          v-bind="ariaInput"
          :aria-invalid="failed"
          @change="onChange"
        >
          <option value="" :disabled="true">-----</option>
          <option
            v-for="option in field.options"
            v-bind:key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>
        <span
          v-bind="ariaMsg"
          aria-live="assertive"
          class="text-sm text-red-700"
        >
          {{ errors[0] }}
        </span>
      </ValidationProvider>
      <CheckboxField
        v-if="field.type == 'checkbox'"
        :id="field.id"
        :name="field.id"
        :label="field.label"
        :legend="`${field.label}${field.required ? '*' : ''}`"
        :extraInformation="field.extra_information"
        :options="field.options"
        :required="field.required"
        :value="response[field.id]"
        :disabled="disabled"
        @change="onChange"
      />
      <RadioField
        v-if="field.type == 'radio'"
        :id="field.id"
        :name="field.id"
        :label="field.label"
        :legend="`${field.label}${field.required ? '*' : ''}`"
        :required="field.required"
        :extraInformation="field.extra_information"
        :options="field.options"
        :value="response[field.id]"
        :disabled="disabled"
        @change="onChange"
      />
      <ArrayField
        v-if="field.type == 'array'"
        :id="field.id"
        :name="field.id"
        :required="field.required"
        :fields="field.fields"
        :initialValue="
          response[field.id] ? Object.values(response[field.id]) : [{}]
        "
        :disabled="disabled"
        @change="onChange"
      />
      <FileField
        v-if="field.type == 'file'"
        class="form-input block w-full border-gray-500 focus:border-blue-600 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
        :id="field.id"
        :name="field.id"
        type="file"
        max-size="5120"
        :required="field.required"
        :multiple="field.multiple"
        :aria-describedby="field.helper ? `${field.id}-helper` : null"
        :initialValue="response[field.id] ? response[field.id]['_files'] : []"
        :csrftoken="csrftoken"
        :disabled="disabled"
        @change="onChange"
      />
      <!-- eslint-disable vue/no-mutating-props -->
      <ValidationProvider
        v-slot="{ errors, failed, ariaMsg, ariaInput }"
        :vid="field.id"
        :name="field.label"
        v-if="
          ![
            'file',
            'longtext',
            'dropdown',
            'checkbox',
            'radio',
            'array',
          ].includes(field.type)
        "
      >
        <input
          :id="field.id"
          :name="field.id"
          class="form-input block w-full border-gray-500 focus:border-blue-600 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          :type="field.type ? field.type : 'text'"
          :required="field.required ? true : false"
          :autocomplete="field.autocomplete ? field.autocomplete : null"
          :min="field.min"
          :max="field.max"
          :step="field.step"
          :disabled="disabled"
          v-model="response[field.id]"
          v-bind="ariaInput"
          :aria-invalid="failed"
          @change="onChange"
        />
        <span
          v-bind="ariaMsg"
          aria-live="assertive"
          class="text-sm text-red-700"
        >
          {{ errors[0] }}
        </span>
        <p
          v-if="field.helper"
          class="mt-2 text-sm text-gray-500 whitespace-pre-wrap"
          :id="`${field.id}-helper`"
        >
          {{ field.helper }}
        </p>
      </ValidationProvider>
    </div>
  </div>
</template>

<script>
import VueMarkdown from 'vue-markdown-render';
import { ValidationProvider } from 'vee-validate';

import ArrayField from './ArrayField';
import CheckboxField from './CheckboxField';
import RadioField from './RadioField';
import FileField from './FileField';

export default {
  name: 'FieldComponent',
  components: {
    VueMarkdown,
    ValidationProvider,
    ArrayField,
    CheckboxField,
    RadioField,
    FileField,
  },
  props: {
    field: Object,
    number: Number,
    response: Object,
    csrftoken: String,
    disabled: Boolean,
  },
  data() {
    return {
      showExtraInformation: false,
    };
  },
  methods: {
    onChange(e) {
      this.$emit('change', e);
    },
    toggleExtraInformation() {
      this.showExtraInformation = !this.showExtraInformation;
    },
  },
};
</script>
